export const CE_DIRECTORY_GROUPS = ['all', 'clinical', 'healthy_living', 'business_growth'] as const;
export const CE_DIRECTORY_FORMATS = ['all', 'in_person', 'live_webinar', 'on_demand'] as const;
export const CE_DIRECTORY_SCOPES = ['city', 'province_state', 'country', 'north_america'] as const;

export type CeDirectoryGroup = typeof CE_DIRECTORY_GROUPS[number];
export type CeDirectoryFormatFilter = typeof CE_DIRECTORY_FORMATS[number];
export type CeDirectoryFormat = Exclude<CeDirectoryFormatFilter, 'all'>;
export type CeDirectoryScope = typeof CE_DIRECTORY_SCOPES[number];
export type CeDirectoryAudience = 'hygienist' | 'assistant' | 'dental_office';
export type CeDirectorySourceSurface = 'talent' | 'office';
export type CeDirectoryCountryCode = 'CA' | 'US';

export type CeDirectoryFilters = {
  group: CeDirectoryGroup;
  format: CeDirectoryFormatFilter;
  scope: CeDirectoryScope;
};

export type CeDirectoryRecognition = {
  key: string;
  label: string;
  logoUrl: string | null;
  officialStatement: string;
  requiredDisclosure: string;
  courseReference: string | null;
};

export type CeDirectoryListing = {
  id: number;
  title: string;
  description: string | null;
  category: {
    key: string;
    label: string;
    group: Exclude<CeDirectoryGroup, 'all'> | null;
  };
  audienceTags: Array<'dentist' | 'hygienist' | 'assistant' | 'admin_office_manager'>;
  format: CeDirectoryFormat;
  schedule: {
    startAtUtc: string | null;
    endAtUtc: string | null;
    eventTimezone: string | null;
    registrationDeadlineUtc: string | null;
  };
  location: {
    city: string | null;
    provinceState: string | null;
    countryCode: CeDirectoryCountryCode | null;
  } | null;
  ce: {
    points: number;
    displayText: string;
    disclosure: string;
    recognition: CeDirectoryRecognition | null;
  };
  priceDisplayText: string | null;
  provider: {
    name: string;
    blurb: string | null;
    logoUrl: string | null;
  };
  courseImageUrl: string | null;
};

export type CeDirectorySafeLocation = {
  id: number;
  label: string;
  city: string | null;
  provinceState: string | null;
  countryCode: CeDirectoryCountryCode | null;
};

export type CeDirectoryResponse = {
  listings: CeDirectoryListing[];
  meta: {
    audience: CeDirectoryAudience;
    selectedLocation: CeDirectorySafeLocation | null;
    scope: {
      requested: CeDirectoryScope;
      effective: CeDirectoryScope;
      cityAvailable: boolean;
      fallbackApplied: boolean;
      context: {
        city: string | null;
        provinceState: string | null;
        countryCode: CeDirectoryCountryCode | null;
        radiusKm: 250;
      };
    };
    filters: {
      q: string | null;
      group: CeDirectoryGroup;
      format: CeDirectoryFormatFilter;
      groups: readonly CeDirectoryGroup[];
      formats: readonly CeDirectoryFormatFilter[];
      scopes: readonly CeDirectoryScope[];
      categories: Array<{
        key: string;
        label: string;
        group: Exclude<CeDirectoryGroup, 'all'> | null;
      }>;
      recognitionPrograms: Array<{ key: string; label: string }>;
    };
    pagination: {
      page: number;
      pageSize: 20;
      total: number;
      hasNextPage: boolean;
    };
  };
};

export type CeDirectoryQuery = CeDirectoryFilters & {
  q?: string;
  page?: number;
  locationId?: number;
};

/**
 * Shared Wave 2B interface. T2-6821 owns its office wrapper and passes the
 * selected Location; it must not fork the directory implementation.
 */
export type CeDirectoryProps = {
  userId: number | string;
  sourceSurface: CeDirectorySourceSurface;
  locationId?: number;
  onAvailabilityChange?: (enabled: boolean) => void;
  onLocationContextInvalid?: (event: {
    reason: 'selection_required' | 'access_denied';
    locations?: CeDirectorySafeLocation[];
  }) => void;
};
